import { DND_DRAGGING_ATTR } from './attributes'
import type { ScrollParent } from './autoScroll'
import { clearDropTarget } from './dropTarget'
import type { Gesture } from './scope'

// The one running gesture and everything it set up. Module-level state only;
// no document/window access at import time, so importing this file during SSR
// is safe. The DOM is only touched after a press or lift event.

export type PendingDrag = Gesture & {
    kind: 'pending'
    /** The pointer that pressed; other fingers, pens, or mice must not steer or drop this drag. */
    pointerId: number
    startX: number
    startY: number
}

export type ActiveDrag = Gesture & {
    kind: 'active'
    pointerId: number
    ghost: HTMLElement
    offsetX: number
    offsetY: number
    pointerX: number
    pointerY: number
    scrollParents: ScrollParent[]
    /** Pending requestAnimationFrame id, or null when no frame is scheduled. */
    frameId: number | null
    stopClassSync: () => void
    savedBodyUserSelect: string
}

// Candidates are in DOM order, so arrow-key order follows document order; on a
// 2D grid that means row-major order, and geometric nearest-neighbour is future work.
export type KeyboardDrag = Gesture & {
    kind: 'keyboard'
    candidates: HTMLElement[]
    /** Slot position in `candidates`, not an element: a re-render between key presses may move elements to other slots. */
    candidateIndex: number
    /**
     * Unfiltered query behind `candidates`, in document order. The next arrow
     * re-queries and reuses `candidates` when the match list is identical, so
     * steady-state arrows skip the per-candidate scope filter; any add, remove,
     * or reorder re-runs it.
     */
    candidateQuery: Element[]
}

export type RunningGesture = PendingDrag | ActiveDrag | KeyboardDrag

export type WindowListeners = {
    pointermove: (event: PointerEvent) => void
    pointerup: (event: PointerEvent) => void
    pointercancel: (event: PointerEvent) => void
    keydown: (event: KeyboardEvent) => void
    blur: () => void
    dragstart: (event: DragEvent) => void
    focusin: (event: FocusEvent) => void
}

// At most one gesture runs at a time; a press or lift is ignored while this is set.
let running: RunningGesture | null = null
// Window listeners live exactly as long as `running` is set: attached when a
// press or lift fills it, detached by endGesture.
let attached: WindowListeners | null = null

export function runningGesture(): RunningGesture | null {
    return running
}

// Not-yet-dragging (a press still inside the movement threshold) and no gesture
// share one identity, so neither publishes a change to useIsDragging.
const NOT_DRAGGED = Symbol('not-dragged')

function draggedPayload(gesture: RunningGesture | null): unknown {
    if (gesture === null || gesture.kind === 'pending') return NOT_DRAGGED
    return gesture.payload
}

const draggedPayloadListeners = new Set<() => void>()

export function subscribeDraggedPayload(listener: () => void): () => void {
    draggedPayloadListeners.add(listener)
    return () => {
        draggedPayloadListeners.delete(listener)
    }
}

function notifyDraggedPayload(): void {
    for (const listener of [...draggedPayloadListeners]) listener()
}

function publishDraggedPayload(previous: RunningGesture | null, next: RunningGesture | null): void {
    if (draggedPayload(previous) !== draggedPayload(next)) notifyDraggedPayload()
}

/** True when `payload` is the object this kit is actively dragging. A pending press is false. */
export function isDraggedPayload(kitId: string, payload: unknown): boolean {
    const gesture = running
    if (gesture === null || gesture.kind === 'pending' || gesture.scope.kitId !== kitId) return false
    return gesture.payload === payload
}

/** Starts a gesture, or moves the running one to its next kind (pending to active). */
export function setRunningGesture(gesture: RunningGesture): void {
    const previous = running
    running = gesture
    publishDraggedPayload(previous, gesture)
}

export function attachWindowListeners(listeners: WindowListeners): void {
    attached = listeners
    window.addEventListener('pointermove', listeners.pointermove)
    window.addEventListener('pointerup', listeners.pointerup)
    window.addEventListener('pointercancel', listeners.pointercancel)
    window.addEventListener('keydown', listeners.keydown)
    window.addEventListener('blur', listeners.blur)
    window.addEventListener('dragstart', listeners.dragstart)
    window.addEventListener('focusin', listeners.focusin)
}

function detachWindowListeners(): void {
    if (attached === null) return
    window.removeEventListener('pointermove', attached.pointermove)
    window.removeEventListener('pointerup', attached.pointerup)
    window.removeEventListener('pointercancel', attached.pointercancel)
    window.removeEventListener('keydown', attached.keydown)
    window.removeEventListener('blur', attached.blur)
    window.removeEventListener('dragstart', attached.dragstart)
    window.removeEventListener('focusin', attached.focusin)
    attached = null
}

export function setBodyDragging(active: boolean): void {
    if (active) document.body.setAttribute(DND_DRAGGING_ATTR, 'true')
    else document.body.removeAttribute(DND_DRAGGING_ATTR)
}

export function focusIfReachable(element: HTMLElement): void {
    // tabIndex >= 0 covers both explicit tabindex and natively focusable elements.
    if (element.tabIndex >= 0) element.focus()
}

type EndGestureOptions = {
    /** False when the user moved focus away themselves; taking it back would undo their move. */
    restoreFocus?: boolean
}

// Ends whichever gesture runs and undoes exactly what that kind of gesture set up.
export function endGesture({ restoreFocus = true }: EndGestureOptions = {}): void {
    const ending = running
    if (ending === null) return
    if (ending.kind === 'active' && ending.frameId !== null) window.cancelAnimationFrame(ending.frameId)
    // Clear while the gesture is still set so the leave callback keeps its context.
    clearDropTarget(ending)
    if (ending.kind === 'active') {
        // Disconnect before React clears the dragging class, so that update is not copied onto a ghost we are about to remove.
        ending.stopClassSync()
        ending.ghost.remove()
        document.body.style.userSelect = ending.savedBodyUserSelect
    }
    if (ending.kind !== 'pending') setBodyDragging(false)
    running = null
    detachWindowListeners()
    // Return focus so keyboard users resume where the lift started.
    if (restoreFocus && ending.kind === 'keyboard' && ending.sourceElement.isConnected) focusIfReachable(ending.sourceElement)
    // running is already clear, so a sync re-render from this publish sees the drag as over.
    publishDraggedPayload(ending, null)
}
